import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader';
import Footer from '../../components/Footer';
import inspectionService from '../../services/inspectionService';
import './ValidateInspectionPage.css';
import logger from '../../utils/logger';
import { useSpinner } from '../../context/SpinnerContext';

const ValidateInspectionPage = () => {
    const { id } = useParams();
    const [validationData, setValidationData] = useState(null);
    const [error, setError] = useState(null);
    const { showSpinner, hideSpinner } = useSpinner();

    const loadedIdRef = useRef(null);

useEffect(() => {
    if (!id) {
        return;
    }

    if (loadedIdRef.current === id) {
        return;
    }

    loadedIdRef.current = id;

    const loadValidationData = async () => {
        try {
            showSpinner('Validando inspección...');
            logger.info('Cargando datos de validación...', {
                inspectionId: id,
            }, 'ValidateInspectionPage');
            const result = await inspectionService.validateInspection(id);
            setValidationData(result.data);
        } catch (err) {
            logger.error('Error cargando validación:', err, null, 'ValidateInspectionPage');
            setError(err.message || 'Error al validar la inspección');
        } finally {
            hideSpinner();
        }
    };

    loadValidationData();
}, [id, showSpinner, hideSpinner]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const formatStatus = (status) => {
        const statusMap = {
            'aprobado': 'Aprobado',
            'rechazado': 'Rechazado',
            'pendiente': 'Pendiente',
            'en-proceso': 'En Proceso',
            'pendiente-revision': 'Pendiente Revisión',
            'pendiente-usuario': 'Pendiente Usuario'
        };
        return statusMap[status?.toLowerCase()] || status || 'N/A';
    };

    if (error || !validationData) {
        return (
            <div className="validate-inspection-page">
                <PublicHeader />
                <main className="validate-content-wrapper">
                    <div className="error-container">
                        <div className="error-icon">
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        </div>
                        <h2>Error al Validar Inspección</h2>
                        <p>{error || 'No se pudo encontrar la inspección'}</p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="validate-inspection-page">
            <PublicHeader />
            <main className="validate-content-wrapper">
                <div className="validate-container">
                    {/* Header de Validación */}
                    <div className="validate-header">
                        <div className="success-icon">
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                        </div>
                        <h1>Informe Verificado</h1>
                        <p className="validate-subtitle">
                            Este informe ha sido validado y es auténtico. La inspección fue realizada mediante la plataforma IVISNK9.
                        </p>
                    </div>

                    {/* Información del Vehículo */}
                    <div className="validate-section">
                        <h2 className="section-title">Información del Vehículo</h2>
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Placa:</span>
                                <span className="info-value">{validationData.vehicleData.licensePlate}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">VIN:</span>
                                <span className="info-value">{validationData.vehicleData.vin}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Chasis:</span>
                                <span className="info-value">{validationData.vehicleData.chassis}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Motor:</span>
                                <span className="info-value">{validationData.vehicleData.engine}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Año:</span>
                                <span className="info-value">{validationData.vehicleData.year}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Marca:</span>
                                <span className="info-value">{validationData.vehicleData.brand}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Línea:</span>
                                <span className="info-value">{validationData.vehicleData.line}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Tipo:</span>
                                <span className="info-value">{validationData.vehicleData.type}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Color:</span>
                                <span className="info-value">{validationData.vehicleData.color}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Combustible:</span>
                                <span className="info-value">{validationData.vehicleData.fuel}</span>
                            </div>
                        </div>
                    </div>

                    {/* Fechas Relevantes */}
                    <div className="validate-section">
                        <h2 className="section-title">Fechas Relevantes</h2>
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Fecha de Creación:</span>
                                <span className="info-value">{formatDate(validationData.dates.createdAt)}</span>
                            </div>
                            {validationData.dates.antecedentsFinishedAt && (
                                <div className="info-item">
                                    <span className="info-label">Antecedentes Finalizados:</span>
                                    <span className="info-value">{formatDate(validationData.dates.antecedentsFinishedAt)}</span>
                                </div>
                            )}
                            {validationData.dates.verdictFinishedAt && (
                                <div className="info-item">
                                    <span className="info-label">Dictamen Finalizado:</span>
                                    <span className="info-value">{formatDate(validationData.dates.verdictFinishedAt)}</span>
                                </div>
                            )}
                            {validationData.dates.visualInspectionFinishedAt && (
                                <div className="info-item">
                                    <span className="info-label">Inspección Visual Finalizada:</span>
                                    <span className="info-value">{formatDate(validationData.dates.visualInspectionFinishedAt)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Estado de la Inspección */}
                    <div className="validate-section">
                        <h2 className="section-title">Estado de la Inspección</h2>
                        <div className="status-grid">
                            <div className="status-item">
                                <span className="status-label">Estado General:</span>
                                <span className={`validate-status-badge status-${validationData.status.overall.toLowerCase().replace('-', '_')}`}>
                                    {formatStatus(validationData.status.overall)}
                                </span>
                            </div>
                            <div className="status-item">
                                <span className="status-label">Antecedentes:</span>
                                <span className={`validate-status-badge status-${validationData.status.antecedents.toLowerCase().replace('-', '_')}`}>
                                    {formatStatus(validationData.status.antecedents)}
                                </span>
                            </div>
                            <div className="status-item">
                                <span className="status-label">Dictamen:</span>
                                <span className={`validate-status-badge status-${validationData.status.verdict.toLowerCase().replace('-', '_')}`}>
                                    {formatStatus(validationData.status.verdict)}
                                </span>
                            </div>
                            {validationData?.inspectionId?.startsWith('INS-') && (
                                <div className="status-item">
                                    <span className="status-label">Inspección visual (latonería, pintura, vidrios):</span>
                                    <span className={`validate-status-badge status-${(validationData?.status?.visualInspection).toLowerCase().replace('-', '_')}`}>
                                        {formatStatus(validationData?.status?.visualInspection)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Información Adicional */}
                    <div className="validate-section">
                        <h2 className="section-title">Información Adicional</h2>
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">ID de Inspección:</span>
                                <span className="info-value">{validationData.inspectionId}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Compañía de Seguros:</span>
                                <span className="info-value">{validationData.partnerData.insuranceCompany}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default ValidateInspectionPage;